/**
 * @file: OrderChatFab.tsx
 *
 * The order's "internal group chat" entry, a floating button stacked one
 * button-height above the AI assistant FAB. Resolves the order's room: if it
 * exists, opens it in the floating chat; otherwise shows a member sheet — the
 * order's involved staff (advisor + assigned technicians) pre-selected on
 * top, the rest of the org contacts below to add — and creates the room on
 * confirm, then opens it. The owner (current user) is added server-side; at
 * least one OTHER member is required.
 */

import { Typography } from '@euler/app/components/typography';
import { useFloatingChat } from '@euler/app/flows/chat/lib/floating/context';
import { Avatar } from '@euler/components/Avatar';
import { BottomPanel } from '@euler/components/BottomPanel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useAlert } from '@euler/functions/ux/useAlert';
import { useServiceFactory } from '@euler/services/factory';
import { Check, MessagesSquare } from 'lucide-react-native';
import { FC, memo, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

interface PickerRow {
  memberId: number;
  name: string;
  avatar: string | null;
  /** Comma-separated OrgUserRoleType codes. */
  role: string;
}

const ROLE_LABEL: Record<string, string> = {
  admin: '管理员',
  tech: '技师',
  service_agent: '服务顾问',
  manager: '店长',
  agent_manager: '顾问主管',
  quoter: '报价员',
  financial: '财务',
  quality_inspector: '质检员',
};

/** Localize the comma-separated role codes; every member has at least one. */
function roleText(role: string): string {
  return role
    .split(',')
    .map(r => r.trim())
    .filter(Boolean)
    .map(r => ROLE_LABEL[r] ?? r)
    .join('、');
}

export const OrderChatFab: FC<{ orderNo: string }> = memo(({ orderNo }) => {
  const { rpx } = useResponsiveContext();
  const insets = useSafeAreaInsets();
  const { chatService } = useServiceFactory();
  const { openChat } = useFloatingChat();
  const showAlert = useAlert();
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sheetVisible, setSheetVisible] = useState(false);
  const [involved, setInvolved] = useState<PickerRow[]>([]);
  const [others, setOthers] = useState<PickerRow[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const onPress = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const existing = await chatService.resolveOrderRoom(orderNo);
      if (existing) {
        openChat({ kind: 'room', roomId: existing.roomId });
        return;
      }
      const [suggested, contacts] = await Promise.all([
        chatService.listOrderSuggestedMembers(orderNo),
        chatService.listContacts(),
      ]);
      const involvedIds = new Set(suggested.map(m => m.memberId));
      setInvolved(
        suggested.map(m => ({
          memberId: m.memberId,
          name: m.name,
          avatar: m.avatarUrl,
          role: m.role,
        })),
      );
      setOthers(
        contacts
          .filter(c => !involvedIds.has(c.memberId))
          .map(c => ({
            memberId: c.memberId,
            name: c.name,
            avatar: c.avatar,
            role: c.role,
          })),
      );
      setSelected(new Set(involvedIds));
      setSheetVisible(true);
    } catch (e) {
      void showAlert({
        title: '打开群聊失败',
        message: String((e as Error)?.message ?? e),
      });
    } finally {
      setBusy(false);
    }
  };

  const toggle = (memberId: number) =>
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });

  const create = async () => {
    if (creating || selected.size === 0) return;
    setCreating(true);
    try {
      const { roomId } = await chatService.createOrderGroupChatRoom({
        orderNo,
        members: [...selected].map(memberId => ({
          type: 'org_member' as const,
          memberId,
        })),
      });
      setSheetVisible(false);
      openChat({ kind: 'room', roomId });
    } catch (e) {
      void showAlert({
        title: '创建群聊失败',
        message: String((e as Error)?.message ?? e),
      });
    } finally {
      setCreating(false);
    }
  };

  const renderRow = (row: PickerRow) => {
    const on = selected.has(row.memberId);
    const subtitle = roleText(row.role);
    return (
      <TouchableOpacity
        key={row.memberId}
        onPress={() => toggle(row.memberId)}
        activeOpacity={0.7}
        css={`
          flex-direction: row;
          align-items: center;
          padding: ${rpx(8)}px 0;
        `}
      >
        <Avatar
          uri={row.avatar ?? undefined}
          name={row.name}
          style={{
            width: rpx(36),
            height: rpx(36),
            borderRadius: rpx(18),
            marginRight: rpx(10),
          }}
        />
        <View style={{ flex: 1 }}>
          <Typography.Text size={rpx(14)} color="#111" numberOfLines={1}>
            {row.name}
          </Typography.Text>
          {subtitle ? (
            <Typography.Text size={rpx(11)} color="#9ca3af" numberOfLines={1}>
              {subtitle}
            </Typography.Text>
          ) : null}
        </View>
        <View
          style={{
            width: rpx(20),
            height: rpx(20),
            borderRadius: rpx(10),
            borderWidth: 1.5,
            borderColor: on ? '#3b82f6' : '#d1d5db',
            backgroundColor: on ? '#3b82f6' : 'transparent',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {on ? <Check color="#fff" size={rpx(13)} strokeWidth={3} /> : null}
        </View>
      </TouchableOpacity>
    );
  };

  const sectionLabel = (text: string) => (
    <Typography.Text
      size={rpx(12)}
      color="#9ca3af"
      style={{ paddingTop: rpx(10), paddingBottom: rpx(2) }}
    >
      {text}
    </Typography.Text>
  );

  const fabSize = rpx(52);

  return (
    <>
      {/* Stacked one AI-FAB height (52) + gap above the AI assistant FAB,
          which sits at bottom = insets.bottom + 80. */}
      <View
        style={[
          styles.fabContainer,
          {
            right: rpx(16),
            bottom: insets.bottom + rpx(80) + fabSize + rpx(12),
          },
        ]}
      >
        <TouchableOpacity
          activeOpacity={0.85}
          onPress={onPress}
          style={[
            styles.fab,
            { width: fabSize, height: fabSize, borderRadius: fabSize / 2 },
          ]}
        >
          {busy ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <MessagesSquare color="#fff" size={rpx(22)} strokeWidth={2.2} />
          )}
        </TouchableOpacity>
      </View>

      <BottomPanel
        visible={sheetVisible}
        onClose={() => setSheetVisible(false)}
      >
        <View style={{ flex: 1, paddingBottom: rpx(12) }}>
          <Typography.Text
            size={rpx(16)}
            color="#111"
            style={{
              textAlign: 'center',
              paddingVertical: rpx(12),
              paddingHorizontal: rpx(16),
            }}
          >
            选择群成员
          </Typography.Text>
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ paddingHorizontal: rpx(16) }}
          >
            {involved.length > 0 ? sectionLabel('相关成员') : null}
            {involved.map(renderRow)}
            {others.length > 0 ? sectionLabel('其他联系人') : null}
            {others.map(renderRow)}
          </ScrollView>
          <TouchableOpacity
            onPress={create}
            disabled={selected.size === 0 || creating}
            activeOpacity={0.8}
            style={{
              marginTop: rpx(16),
              marginHorizontal: rpx(16),
              paddingVertical: rpx(12),
              borderRadius: rpx(8),
              alignItems: 'center',
              backgroundColor: selected.size === 0 ? '#9ec5fe' : '#3b82f6',
            }}
          >
            {creating ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <Typography.Text size={rpx(15)} color="#fff">
                创建群聊（{selected.size}）
              </Typography.Text>
            )}
          </TouchableOpacity>
        </View>
      </BottomPanel>
    </>
  );
});

const styles = StyleSheet.create({
  fabContainer: {
    position: 'absolute',
    zIndex: 999,
  },
  fab: {
    backgroundColor: '#3b82f6',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#1e3a8a',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.35,
    shadowRadius: 14,
    elevation: 20,
  },
});
